import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardPeriod } from '@control-center/shared';

import {
  fetchChargesToday,
  fetchDashboardAlerts,
  fetchDashboardKpis,
  fetchDivergenceAlerts,
  fetchInadimplentes,
  fetchRevenueByProduct,
  fetchRevenueMonthly,
  resendChargeReminder,
  resolveDivergenceAlert,
} from '../lib/dashboard-api';

export const dashboardKeys = {
  all: ['dashboard'] as const,
  kpis: (period: DashboardPeriod) => [...dashboardKeys.all, 'kpis', period] as const,
  alerts: () => [...dashboardKeys.all, 'alerts'] as const,
  inadimplentes: (limit: number) => [...dashboardKeys.all, 'inadimplentes', limit] as const,
  chargesToday: (limit: number) => [...dashboardKeys.all, 'charges-today', limit] as const,
  revenueByProduct: (limit: number) => [...dashboardKeys.all, 'revenue-by-product', limit] as const,
  revenueMonthly: (months: number) => [...dashboardKeys.all, 'revenue-monthly', months] as const,
  divergenceAlerts: (resolved: boolean) => [...dashboardKeys.all, 'divergence', resolved] as const,
};

export function useDashboardKpis(period: DashboardPeriod = 'current_month') {
  return useQuery({
    queryKey: dashboardKeys.kpis(period),
    queryFn: () => fetchDashboardKpis(period),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useDashboardAlerts() {
  return useQuery({
    queryKey: dashboardKeys.alerts(),
    queryFn: fetchDashboardAlerts,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useInadimplentes(limit = 10) {
  return useQuery({
    queryKey: dashboardKeys.inadimplentes(limit),
    queryFn: () => fetchInadimplentes(limit),
    staleTime: 60_000,
  });
}

export function useResendChargeReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chargeId: string) => resendChargeReminder(chargeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}

export function useChargesToday(limit = 50) {
  return useQuery({
    queryKey: dashboardKeys.chargesToday(limit),
    queryFn: () => fetchChargesToday(limit),
    staleTime: 30_000,
  });
}

export function useRevenueByProduct(limit = 10) {
  return useQuery({
    queryKey: dashboardKeys.revenueByProduct(limit),
    queryFn: () => fetchRevenueByProduct(limit),
    staleTime: 5 * 60_000,
  });
}

export function useRevenueMonthly(months = 12) {
  return useQuery({
    queryKey: dashboardKeys.revenueMonthly(months),
    queryFn: () => fetchRevenueMonthly(months),
    staleTime: 5 * 60_000,
  });
}

export function useDivergenceAlerts(resolved = false) {
  return useQuery({
    queryKey: dashboardKeys.divergenceAlerts(resolved),
    queryFn: () => fetchDivergenceAlerts({ resolved, pageSize: 50 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useResolveDivergenceAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      resolveDivergenceAlert(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}
